"use client";

import { AnimatePresence, motion } from "framer-motion";
import type { MotionValue } from "framer-motion";

import { cn } from "@/lib/utils";

import type { UnderwritingStep } from "./underwriting-engine";
import { Waveform } from "./underwriting-engine-waveform";

interface UnderwritingEngineLeftProps {
  steps: UnderwritingStep[];
  activeStep: number;
  /** Scroll progress 0→1; drives the waveform playhead (animated mode). */
  progress?: MotionValue<number>;
  /** When true, render every step statically (reduced motion / mobile). */
  showAll?: boolean;
  onStepSelect?: (stepIndex: number) => void;
  className?: string;
}

export function UnderwritingEngineLeft({
  steps,
  activeStep,
  progress,
  showAll = false,
  onStepSelect,
  className,
}: UnderwritingEngineLeftProps) {
  if (showAll) {
    return (
      <ol className={cn("flex flex-col gap-8", className)}>
        {steps.map((step) => (
          <li key={step.number} className="flex flex-col gap-2">
            <StepCopy step={step} />
          </li>
        ))}
      </ol>
    );
  }

  const step = steps[Math.min(activeStep, steps.length - 1)];
  if (!step) {
    return null;
  }

  return (
    <div className={cn("flex max-w-[465px] flex-col gap-6", className)}>
      {/* Reserve height so swaps between steps of differing length never reflow. */}
      <div className="relative min-h-[180px]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step.number}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="flex flex-col gap-2"
          >
            <StepCopy step={step} />
          </motion.div>
        </AnimatePresence>
      </div>

      {progress && (
        <Waveform
          progress={progress}
          className="w-[236px]"
          stepCount={steps.length}
          onStepSelect={onStepSelect}
        />
      )}
    </div>
  );
}

function StepCopy({ step }: { step: UnderwritingStep }) {
  return (
    <>
      <p className="text-grey-500 text-[12px] leading-[1.4] font-medium">
        {step.eyebrow}
      </p>
      <p className="text-grey-950 text-[20px] leading-[1.2] tracking-[-0.04em] md:text-[24px]">
        {step.title}
      </p>
    </>
  );
}
